/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * Assignment settings form visibility config
 * @author Satyaki Bose
 * @file assignmentSettingsFormConfig.js
 */

import { defaultConfig } from './assignmentSettingsConstants';

const SECTION_ORDER = ['gradebook', 'dueDates', 'lateWork', 'availability'];

// takes in optional configProp and returns a merged config
export function mergeAssignmentSettingsConfig(configProp = {}) {
  return { ...defaultConfig, ...configProp };
}

export function computeAssignmentSettingsVisibility(effectiveSettings = {}) {
  const showGradebookSection = effectiveSettings.showGradebookSection
    && (effectiveSettings.showRawScoreOption || effectiveSettings.showEqualWeightingOption);

  const showDueDatesSection = effectiveSettings.showDueDatesSection
    && (effectiveSettings.showDefaultDueTimeField || effectiveSettings.showCourseTimeZoneField);

  const showLateWorkRuleContent = effectiveSettings.showLateWorkRuleRow
    && (effectiveSettings.showLateWorkPercentField || effectiveSettings.showLateWorkDeadlineField);

  const showLateWorkSection = effectiveSettings.showLateWorkSection
    && (
      effectiveSettings.showAllowLateWorkCheckbox
      || showLateWorkRuleContent
      || effectiveSettings.showLateWorkCustomDateField
    );

  const showAvailabilitySection = effectiveSettings.showAvailabilitySection
    && effectiveSettings.showAvailabilityField;

  return {
    showAlertBanner: effectiveSettings.showAlertBanner,
    showFormActions: effectiveSettings.showFormActions,
    showCancelButton: effectiveSettings.showCancelButton && effectiveSettings.showFormActions,
    showSaveButton: effectiveSettings.showSaveButton && effectiveSettings.showFormActions,
    showGradebookInfoModal: effectiveSettings.showGradebookInfoModal,
    disableAllFields: effectiveSettings.disableAllFields,

    isGradebookSectionVisible: showGradebookSection,
    isGradebookSectionTitleVisible: showGradebookSection && effectiveSettings.showGradebookSectionTitle,
    isGradebookInfoButtonVisible: showGradebookSection && effectiveSettings.showGradebookInfoButton,
    isRawScoreOptionVisible: showGradebookSection && effectiveSettings.showRawScoreOption,
    isEqualWeightingOptionVisible: showGradebookSection && effectiveSettings.showEqualWeightingOption,
    isEqualWeightingBaseValueVisible: showGradebookSection
      && effectiveSettings.showEqualWeightingOption
      && effectiveSettings.showEqualWeightingBaseValue,

    isDueDatesSectionVisible: showDueDatesSection,
    isDueDatesSectionTitleVisible: showDueDatesSection && effectiveSettings.showDueDatesSectionTitle,
    isDefaultDueTimeFieldVisible: showDueDatesSection && effectiveSettings.showDefaultDueTimeField,
    isCourseTimeZoneFieldVisible: showDueDatesSection && effectiveSettings.showCourseTimeZoneField,

    isLateWorkSectionVisible: showLateWorkSection,
    isLateWorkSectionTitleVisible: showLateWorkSection && effectiveSettings.showLateWorkSectionTitle,
    isAllowLateWorkCheckboxVisible: showLateWorkSection && effectiveSettings.showAllowLateWorkCheckbox,
    isLateWorkRuleRowVisible: showLateWorkSection && showLateWorkRuleContent,
    isLateWorkPercentFieldVisible: showLateWorkSection
      && showLateWorkRuleContent
      && effectiveSettings.showLateWorkPercentField,
    isLateWorkDeadlineFieldVisible: showLateWorkSection
      && showLateWorkRuleContent
      && effectiveSettings.showLateWorkDeadlineField,
    isLateWorkCustomDateFieldVisible: showLateWorkSection && effectiveSettings.showLateWorkCustomDateField,

    isAvailabilitySectionVisible: showAvailabilitySection,
    isAvailabilitySectionTitleVisible: showAvailabilitySection && effectiveSettings.showAvailabilitySectionTitle,
    isAvailabilityFieldVisible: showAvailabilitySection && effectiveSettings.showAvailabilityField
  };
}

export function resolveAssignmentSettingsVisibility(configProp = {}) {
  return computeAssignmentSettingsVisibility(
    mergeAssignmentSettingsConfig(configProp)
  );
}

export function getVisibleSections(config) {
  const visibility = resolveAssignmentSettingsVisibility(config);

  const sectionVisibility = {
    gradebook: visibility.isGradebookSectionVisible,
    dueDates: visibility.isDueDatesSectionVisible,
    lateWork: visibility.isLateWorkSectionVisible,
    availability: visibility.isAvailabilitySectionVisible
  };

  return SECTION_ORDER.filter((sectionKey) => sectionVisibility[sectionKey]);
}
